import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { api, apiErr } from "@/lib/api";
import { toast } from "sonner";
import { X, Loader2, Download, FileText } from "lucide-react";

export default function PdfViewer({ relUrl, title = "Report", filename = "report.pdf", onClose }) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [shown, setShown] = useState(false);

  useEffect(() => {
    let objUrl;
    let active = true;
    (async () => {
      try {
        const res = await api.get(relUrl, { responseType: "blob" });
        objUrl = URL.createObjectURL(res.data);
        if (active) setUrl(objUrl);
        else URL.revokeObjectURL(objUrl);
      } catch (e) {
        toast.error(apiErr(e));
        onClose();
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; if (objUrl) URL.revokeObjectURL(objUrl); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [relUrl]);

  useEffect(() => {
    const raf = requestAnimationFrame(() => setShown(true));
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { cancelAnimationFrame(raf); window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[80]" data-testid="pdf-viewer">
      <div className={`absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity duration-300 ${shown ? "opacity-100" : "opacity-0"}`} onClick={onClose} />
      <div className={`absolute top-0 right-0 h-full w-full sm:max-w-3xl bg-[#0E0E0E] border-l border-[#2E2E2E] shadow-2xl flex flex-col transition-transform duration-300 ease-out ${shown ? "translate-x-0" : "translate-x-full"}`}>
        <div className="shrink-0 flex items-center justify-between gap-3 px-4 sm:px-5 py-3.5 border-b border-[#2E2E2E]">
          <div className="flex items-center gap-2.5 min-w-0">
            <div className="h-9 w-9 rounded-xl bg-[#E11D2A]/15 flex items-center justify-center shrink-0">
              <FileText className="h-4 w-4 text-[#E11D2A]" />
            </div>
            <p className="font-heading font-semibold truncate">{title}</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {url && (
              <a data-testid="pdf-viewer-download" href={url} download={filename}
                className="h-10 px-3 rounded-xl border border-[#2E2E2E] bg-[#171717] flex items-center gap-2 text-sm hover:border-[#E11D2A]/60 transition-colors" title="Download">
                <Download className="h-4 w-4 text-[#A3A3A3]" /> <span className="hidden sm:inline">Download</span>
              </a>
            )}
            <button data-testid="pdf-viewer-close" onClick={onClose}
              className="h-10 px-4 rounded-xl bg-[#E11D2A] text-white font-medium flex items-center gap-2 hover:bg-[#B91C1C] transition-colors">
              <X className="h-4 w-4" /> Close
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 bg-[#171717] relative">
          {loading && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-[#6B6B6B]">
              <Loader2 className="h-7 w-7 animate-spin text-[#E11D2A]" />
              <p className="text-sm">Loading report…</p>
            </div>
          )}
          {url && (
            <iframe data-testid="pdf-viewer-frame" title={title} src={url} className="w-full h-full border-0" />
          )}
        </div>

        {url && (
          <div className="shrink-0 sm:hidden px-4 py-2 border-t border-[#2E2E2E] text-center">
            <a href={url} target="_blank" rel="noopener noreferrer" className="text-xs text-[#A3A3A3] underline">
              Trouble viewing? Open the PDF in a new tab
            </a>
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}
